import React from 'react';
import { Gauge, CircuitBoard, DoorOpen, ShieldAlert, Activity, Scan, Shield, CheckCircle } from 'lucide-react';
import { safetyFeatures } from '../data/mock';

const TechnologySection = () => {
  const iconMap = {
    Gauge: Gauge,
    CircuitBoard: CircuitBoard,
    DoorOpen: DoorOpen,
    ShieldAlert: ShieldAlert,
    Activity: Activity,
    Scan: Scan
  };

  return (
    <section id="technology" className="section-padding bg-[#FDFBF7]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block text-[#B8956B] font-medium text-sm uppercase tracking-[0.2em] mb-4">
            Technology & Safety
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#252A32] mb-6 heading-display">
            Engineering Components That <span className="text-[#B8956B] italic">Protect</span>
          </h2>
          <div className="elegant-divider mx-auto mb-6" />
          <p className="text-[#404752] text-lg">
            We use only the finest components from trusted global suppliers, ensuring 
            every elevator meets the highest safety and performance standards.
          </p>
        </div>

        {/* Safety Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {safetyFeatures.map((feature, index) => {
            const IconComponent = iconMap[feature.icon] || Shield;
            return (
              <div 
                key={feature.id}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#B8956B]/20 transition-all hover:shadow-lg"
              >
                <div className="safety-icon w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                  <IconComponent className="w-7 h-7 text-[#B8956B]" />
                </div>
                <h3 className="text-xl font-semibold text-[#252A32] mb-3">{feature.title}</h3>
                <p className="text-[#404752] leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Highlight Box */}
        <div className="bg-[#B8956B] rounded-3xl p-10 lg:p-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
                <ShieldAlert className="w-4 h-4 text-white" />
                <span className="text-white font-medium text-sm tracking-wide">Becone Innovation</span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-semibold text-white mb-5 heading-display">
                Never Get Stuck in Our Elevators
              </h3>
              <p className="text-white/85 text-lg leading-relaxed mb-6">
                Our innovative safety solution allows the elevator to operate even if the ARD 
                (Automatic Rescue Device) fails. This first-of-its-kind feature in India ensures 
                you're never stranded.
              </p>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-white" />
                <span className="text-white font-medium">Ride worry-free, always!</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Speed Governor', desc: 'Prevents overspeeding' },
                { label: 'Counterweights', desc: 'Balanced operation' },
                { label: 'Guide Rails', desc: 'Smooth travel' },
                { label: 'Door Sensors', desc: 'Infrared protection' }
              ].map((item, idx) => (
                <div key={idx} className="bg-white/15 rounded-xl p-5 backdrop-blur-sm">
                  <p className="font-semibold text-white">{item.label}</p>
                  <p className="text-white/70 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Compliance Banner */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-5 bg-[#252A32] rounded-2xl px-10 py-7">
            <Shield className="w-10 h-10 text-[#B8956B]" />
            <div className="text-left">
              <p className="text-white font-semibold text-lg">IS 17900:2022 Compliant</p>
              <p className="text-gray-400 text-sm">All installations meet Indian safety standards</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;
